import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, mediaUrl, BACKEND_URL } from "../../lib/api";
import { invalidateSEOCache } from "../../components/SEOHead";
import { invalidateAnalyticsCache } from "../../components/GTMLoader";
import {
  Loader2, Save, Search, AlertTriangle, AlertCircle, CheckCircle2, ExternalLink,
  X, RotateCcw, Image as ImageIcon, Upload, Info, Sparkles, Copy, Wand2,
  ChevronLeft, ChevronRight, Check,
} from "lucide-react";

const inputCls = "w-full rounded-md border border-aubergine/15 bg-white px-3 py-2 text-sm outline-none focus:border-champagne transition-colors";
const codeInputCls = `${inputCls} font-mono text-xs`;

export default function AdminSEO() {
  const [params] = useSearchParams();
  const initialTab = ["pages", "global", "audit"].includes(params.get("tab")) ? params.get("tab") : "pages";
  const [tab, setTab] = useState(initialTab);
  const [pagesData, setPagesData] = useState({ pages: [], global: null });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // page row
  const [globalDraft, setGlobalDraft] = useState(null);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [globalMsg, setGlobalMsg] = useState("");
  const [audit, setAudit] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/seo/pages");
      setPagesData(r.data);
      setGlobalDraft(r.data.global);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (tab !== "audit") return;
    api.get("/admin/seo/audit").then((r) => setAudit(r.data));
  }, [tab]);

  const filteredPages = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return pagesData.pages;
    return pagesData.pages.filter((p) =>
      `${p.path} ${p.label || ""} ${p.title || ""}`.toLowerCase().includes(term)
    );
  }, [pagesData.pages, q]);

  const saveGlobal = async () => {
    setSavingGlobal(true);
    setGlobalMsg("");
    try {
      await api.put("/admin/seo/global", globalDraft);
      invalidateSEOCache();
      setGlobalMsg("Saved. Site-wide defaults updated.");
      await load();
    } catch (e) {
      setGlobalMsg(e?.response?.data?.detail || "Failed to save");
    } finally { setSavingGlobal(false); }
  };

  return (
    <div data-testid="admin-seo-page">
      <header className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne mb-2">Search &amp; discovery</p>
          <h1 className="font-serif text-3xl text-aubergine">SEO</h1>
          <p className="mt-2 text-sm text-aubergine/65">
            Title tags, meta descriptions, canonical URLs, social share cards and JSON-LD schema, for the whole site and per page.
          </p>
        </div>
      </header>

      <div className="mb-5 flex items-center gap-1 border-b border-aubergine/10">
        {[
          { id: "pages", label: "Pages" },
          { id: "global", label: "Global defaults" },
          { id: "audit", label: "Audit" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`seo-tab-${t.id}`}
            className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? "border-champagne text-aubergine"
                : "border-transparent text-aubergine/55 hover:text-aubergine"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="py-16 flex items-center justify-center text-aubergine/55">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}

      {!loading && tab === "pages" && (
        <PagesTable
          pages={filteredPages}
          q={q}
          setQ={setQ}
          onEdit={setEditing}
        />
      )}

      {!loading && tab === "global" && globalDraft && (
        <GlobalEditor
          value={globalDraft}
          onChange={setGlobalDraft}
          onSave={saveGlobal}
          saving={savingGlobal}
          msg={globalMsg}
        />
      )}

      {!loading && tab === "audit" && (
        <AuditPanel audit={audit} onEditPage={(path) => {
          const row = pagesData.pages.find((p) => p.path === path);
          if (row) setEditing(row);
        }} onAuditRefresh={() => { api.get("/admin/seo/audit").then((r) => setAudit(r.data)); }} />
      )}

      {editing && (
        <PageEditor
          row={editing}
          global={pagesData.global}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pages table
// ---------------------------------------------------------------------------
function PagesTable({ pages, q, setQ, onEdit }) {
  return (
    <div>
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-aubergine/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by path or title…"
            data-testid="seo-pages-search"
            className={`${inputCls} pl-9`}
          />
        </div>
      </div>

      <div className="rounded-lg border border-aubergine/10 bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="seo-pages-table">
          <thead className="bg-bone/60">
            <tr className="text-left text-[11px] font-mono uppercase tracking-[0.12em] text-aubergine/55">
              <th className="px-4 py-3">Page</th>
              <th className="px-4 py-3 hidden md:table-cell">Title tag</th>
              <th className="px-4 py-3 hidden lg:table-cell">Description</th>
              <th className="px-4 py-3">Health</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pages.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-aubergine/55">No matches.</td></tr>
            ) : pages.map((p) => (
              <tr
                key={p.path}
                onClick={() => onEdit(p)}
                data-testid={`seo-page-row-${p.path.replace(/[^a-z0-9]/gi, "-")}`}
                className="border-t border-aubergine/10 hover:bg-bone/40 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-aubergine">{p.label || p.path}</div>
                  <div className="text-[11px] font-mono text-aubergine/55 flex items-center gap-1.5 mt-0.5">
                    {p.path}
                    <TypeBadge type={p.type} />
                  </div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell max-w-xs">
                  <div className="text-xs text-aubergine/75 truncate">
                    {p.title || <span className="italic text-aubergine/35">(inherits global)</span>}
                  </div>
                  {p.title && (
                    <CharCounter value={p.title} max={60} />
                  )}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell max-w-md">
                  <div className="text-xs text-aubergine/65 truncate">
                    {p.description || <span className="italic text-aubergine/35">(inherits global)</span>}
                  </div>
                  {p.description && (
                    <CharCounter value={p.description} max={160} />
                  )}
                </td>
                <td className="px-4 py-3">
                  <HealthPill issues={p.issues || []} hasRecord={p.has_record} />
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); onEdit(p); }}
                    data-testid={`seo-edit-${p.path.replace(/[^a-z0-9]/gi, "-")}`}
                    className="text-champagne hover:text-aubergine font-mono text-[11px] uppercase tracking-wider"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TypeBadge({ type }) {
  const styles = {
    static: "bg-aubergine/8 text-aubergine/70",
    template: "bg-champagne/15 text-champagne",
    article: "bg-champ-light/20 text-aubergine/70",
  };
  const label = { static: "Page", template: "Template", article: "Article" }[type] || type;
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${styles[type] || "bg-aubergine/8 text-aubergine/70"}`}>
      {label}
    </span>
  );
}

function HealthPill({ issues, hasRecord }) {
  if (!hasRecord) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-aubergine/8 text-aubergine/55 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider">
        <span className="h-1.5 w-1.5 rounded-full bg-aubergine/25" /> Inherits
      </span>
    );
  }
  const errors = issues.filter((i) => i.level === "error").length;
  const warns = issues.filter((i) => i.level === "warn").length;
  if (errors > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-signal-red/10 text-signal-red px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider" data-testid="seo-health-errors">
        <AlertCircle className="h-3 w-3" /> {errors} error{errors === 1 ? "" : "s"}
      </span>
    );
  }
  if (warns > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-champagne/20 text-aubergine px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider" data-testid="seo-health-warnings">
        <AlertTriangle className="h-3 w-3" /> {warns} warning{warns === 1 ? "" : "s"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-champagne/15 text-aubergine px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider" data-testid="seo-health-ok">
      <CheckCircle2 className="h-3 w-3 text-champagne" /> Good
    </span>
  );
}

function CharCounter({ value, max, label }) {
  const len = (value || "").length;
  const over = len > max;
  const suffix = label ? ` ${label}` : "";
  return (
    <span
      className={`inline-block mt-1 font-mono text-[10px] ${over ? "text-signal-red" : "text-aubergine/40"}`}
      title={`${len} / ${max} characters${suffix}`}
    >
      {len} / {max}{label ? <span className="ml-1 text-aubergine/45">({label})</span> : null}
    </span>
  );
}

function resolveTitle(raw, global) {
  const tmpl = global?.title_template || "";
  if (!raw) return global?.default_title || "T-REX Network";
  if (tmpl.includes("%s")) return tmpl.replace("%s", raw);
  return raw;
}

// ---------------------------------------------------------------------------
// Global settings editor
// ---------------------------------------------------------------------------
function GlobalEditor({ value, onChange, onSave, saving, msg }) {
  const patch = (k, v) => onChange({ ...value, [k]: v });

  return (
    <div className="max-w-3xl space-y-6" data-testid="seo-global-editor">
      <div className="rounded-lg border border-aubergine/10 bg-white p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-4">Site identity</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Site name">
            <input value={value.site_name || ""} onChange={(e) => patch("site_name", e.target.value)} className={inputCls} data-testid="seo-global-site-name" />
          </Field>
          <Field label="Canonical base URL" hint="Absolute URL, no trailing slash.">
            <input value={value.canonical_base_url || ""} onChange={(e) => patch("canonical_base_url", e.target.value)} className={inputCls} data-testid="seo-global-canonical" />
          </Field>
          <Field label="Default title" hint="Used when a page has no title of its own.">
            <input value={value.default_title || ""} onChange={(e) => patch("default_title", e.target.value)} className={inputCls} data-testid="seo-global-default-title" />
          </Field>
          <Field label="Title template" hint="Use %s where the page title should appear. Leave blank to skip.">
            <input value={value.title_template || ""} onChange={(e) => patch("title_template", e.target.value)} className={inputCls} data-testid="seo-global-title-template" />
          </Field>
        </div>
      </div>

      <div className="rounded-lg border border-aubergine/10 bg-white p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-4">Search &amp; snippets</p>
        <Field label="Default meta description" hint="Under 160 characters. Shown as fallback for pages without their own description.">
          <textarea
            value={value.default_description || ""}
            onChange={(e) => patch("default_description", e.target.value)}
            rows={3}
            className={`${inputCls} resize-none`}
            data-testid="seo-global-default-description"
          />
          <CharCounter value={value.default_description || ""} max={160} />
        </Field>
        <div className="mt-4">
          <Field label="Default keywords" hint="Comma-separated. Optional; search engines mostly ignore these but tools still use them.">
            <input value={value.default_keywords || ""} onChange={(e) => patch("default_keywords", e.target.value)} className={inputCls} data-testid="seo-global-keywords" />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Robots directive" hint="For example: index, follow. Or noindex, nofollow to hide from search results.">
            <input value={value.robots || ""} onChange={(e) => patch("robots", e.target.value)} className={inputCls} data-testid="seo-global-robots" />
          </Field>
        </div>
      </div>

      <div className="rounded-lg border border-aubergine/10 bg-white p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-4">Social cards</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Default OG image URL" hint="Used when a page has no OG image of its own. 1200x630 recommended.">
            <input value={value.default_og_image_url || ""} onChange={(e) => patch("default_og_image_url", e.target.value)} className={inputCls} data-testid="seo-global-og-image-url" />
          </Field>
          <MediaPickerField
            label="Or pick from Media library"
            value={value.default_og_image_media_id || ""}
            onChange={(id) => patch("default_og_image_media_id", id)}
            testid="seo-global-og-image-media"
          />
          <Field label="Twitter handle" hint="Include the @.">
            <input value={value.twitter_handle || ""} onChange={(e) => patch("twitter_handle", e.target.value)} className={inputCls} data-testid="seo-global-twitter" />
          </Field>
          <Field label="Twitter card type">
            <select value={value.twitter_card || "summary_large_image"} onChange={(e) => patch("twitter_card", e.target.value)} className={inputCls} data-testid="seo-global-twitter-card">
              <option value="summary">summary</option>
              <option value="summary_large_image">summary_large_image</option>
            </select>
          </Field>
        </div>
      </div>

      <div className="rounded-lg border border-aubergine/10 bg-white p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-4">Structured data (Organization)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Organization name">
            <input value={value.organization_name || ""} onChange={(e) => patch("organization_name", e.target.value)} className={inputCls} data-testid="seo-global-org-name" />
          </Field>
          <Field label="Organization logo URL">
            <input value={value.organization_logo_url || ""} onChange={(e) => patch("organization_logo_url", e.target.value)} className={inputCls} data-testid="seo-global-org-logo" />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Social profile URLs (sameAs)" hint="One URL per line. LinkedIn, X, GitHub, etc.">
            <textarea
              value={(value.organization_same_as || []).join("\n")}
              onChange={(e) => patch("organization_same_as", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
              rows={4}
              className={`${inputCls} resize-none font-mono text-xs`}
              data-testid="seo-global-org-sameas"
            />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Custom site-wide JSON-LD (advanced)" hint="Overrides the auto-generated Organization/WebSite graph. Must be valid JSON.">
            <textarea
              value={value.custom_site_schema || ""}
              onChange={(e) => patch("custom_site_schema", e.target.value)}
              rows={6}
              className={`${inputCls} resize-y font-mono text-[11px]`}
              placeholder='{ "@context": "https://schema.org", "@type": "Organization", ... }'
              data-testid="seo-global-custom-schema"
            />
          </Field>
        </div>
      </div>

      <SitemapCard />

      <IndexNowCard />

      <AnalyticsCard />

      <ConsentCard />

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="seo-global-save"
          className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2.5 font-mono text-xs disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save global defaults
        </button>
        <SeedButton />
        {msg && (
          <span className="font-mono text-[11px] text-aubergine/65" data-testid="seo-global-msg">
            {msg}
          </span>
        )}
      </div>
    </div>
  );
}

function SeedButton() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const run = async () => {
    if (!window.confirm("Seed curated SEO for the 15 top public routes? Existing overrides are preserved.")) return;
    setBusy(true);
    setMsg("");
    try {
      const r = await api.post("/admin/seo/seed");
      invalidateSEOCache();
      setMsg(`Seeded ${r.data.inserted_count}, skipped ${r.data.skipped_count} (already curated).`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Seed failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="inline-flex items-center gap-2">
      <button
        onClick={run}
        disabled={busy}
        data-testid="seo-global-seed"
        className="inline-flex items-center gap-2 rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-4 py-2.5 font-mono text-xs text-aubergine disabled:opacity-60"
        title="Populate curated title/description defaults for the 15 top-visited public routes."
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-champagne" />}
        Seed 15 curated pages
      </button>
      {msg && <span className="font-mono text-[11px] text-aubergine/65" data-testid="seo-seed-msg">{msg}</span>}
    </div>
  );
}

function SitemapCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    api.get("/admin/sitemap/preview")
      .then((r) => { if (alive) setPreview(r.data); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  const publicUrl = `${BACKEND_URL}/api/sitemap.xml`;
  const copyUrl = () => { navigator.clipboard?.writeText(publicUrl); };
  return (
    <div className="rounded-lg border border-aubergine/10 bg-white p-5" data-testid="seo-sitemap-card">
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-4">Sitemap</p>
      <p className="text-sm text-aubergine/75 mb-3">
        The sitemap auto-regenerates from every managed page, published article and doc. Submit the URL below to Google Search Console and Bing Webmaster Tools.
      </p>
      <div className="flex items-center gap-2 rounded-md border border-aubergine/15 bg-bone/40 px-3 py-2">
        <code className="flex-1 font-mono text-[12px] text-aubergine truncate select-all">{publicUrl}</code>
        <button
          onClick={copyUrl}
          type="button"
          data-testid="seo-sitemap-copy"
          className="inline-flex items-center gap-1 text-aubergine/65 hover:text-aubergine font-mono text-[10px] uppercase tracking-wider"
        >
          <Copy className="h-3 w-3" /> Copy
        </button>
        <a
          href={publicUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="seo-sitemap-open"
          className="inline-flex items-center gap-1 text-aubergine/65 hover:text-champagne font-mono text-[10px] uppercase tracking-wider"
        >
          <ExternalLink className="h-3 w-3" /> Open
        </a>
      </div>
      <div className="mt-2 text-[11px] font-mono text-aubergine/55" data-testid="seo-sitemap-stats">
        {loading ? "Reading current sitemap…" : preview ? `${preview.url_count} URLs · ${(preview.size_bytes / 1024).toFixed(1)} KB` : "Could not read sitemap."}
      </div>
    </div>
  );
}


function IndexNowCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const reload = () => api.get("/admin/seo/indexnow").then((r) => setData(r.data));

  useEffect(() => { reload(); }, []);

  if (!data) {
    return (
      <div className="rounded-lg border border-aubergine/10 bg-white p-5 flex items-center justify-center h-24 text-aubergine/45">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  const { settings, last_success, recent } = data;
  const keyUrl = `${BACKEND_URL}/api/indexnow/${settings.key}.txt`;

  const toggle = async (field) => {
    setBusy(true);
    try {
      await api.patch("/admin/seo/indexnow", { [field]: !settings[field] });
      await reload();
    } finally { setBusy(false); }
  };

  const rotate = async () => {
    if (!window.confirm("Rotate the IndexNow key? You will need to resubmit the new key location to search engines.")) return;
    setBusy(true);
    try {
      await api.post("/admin/seo/indexnow/rotate");
      await reload();
      setMsg("New key generated.");
    } finally { setBusy(false); }
  };

  const pingHome = async () => {
    setBusy(true);
    setMsg("");
    try {
      const canonical = (data?.settings?.canonical_base_url || "").replace(/\/$/, "") || "https://t-rex.network";
      const r = await api.post("/admin/seo/indexnow/ping", { urls: [`${canonical}/`, `${canonical}/articles`] });
      const okEngines = Object.entries(r.data.engines || {}).filter(([, v]) => v.ok).map(([k]) => k);
      const failEngines = Object.entries(r.data.engines || {}).filter(([, v]) => !v.ok).map(([k, v]) => `${k}(${v?.status || "err"})`);
      setMsg(`Ping sent. OK: ${okEngines.join(", ") || "none"}. Failed: ${failEngines.join(", ") || "none"}.`);
      await reload();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Ping failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-aubergine/10 bg-white p-5" data-testid="seo-indexnow-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55">Search engine ping (IndexNow)</p>
          <p className="text-sm text-aubergine/75 mt-1">
            Notify Bing, Yandex, Naver, Seznam and DuckDuckGo the moment content changes. Google discovers via sitemap.xml.
          </p>
        </div>
        {last_success ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-champagne/15 text-aubergine px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider flex-shrink-0" data-testid="seo-indexnow-last">
            <CheckCircle2 className="h-3 w-3 text-champagne" /> Live
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-aubergine/8 text-aubergine/55 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider flex-shrink-0" data-testid="seo-indexnow-last">
            Never pinged
          </span>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 mb-4">
        <label className="flex items-center gap-2 text-sm text-aubergine cursor-pointer" data-testid="seo-indexnow-enabled">
          <input
            type="checkbox"
            checked={!!settings.enabled}
            onChange={() => toggle("enabled")}
            disabled={busy}
            className="accent-champagne"
          />
          IndexNow enabled
        </label>
        <label className="flex items-center gap-2 text-sm text-aubergine cursor-pointer" data-testid="seo-indexnow-auto">
          <input
            type="checkbox"
            checked={!!settings.auto_ping_on_publish}
            onChange={() => toggle("auto_ping_on_publish")}
            disabled={busy}
            className="accent-champagne"
          />
          Auto-ping when an article is published
        </label>
      </div>

      <div className="rounded-md border border-aubergine/15 bg-bone/40 px-3 py-2 mb-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-aubergine/55 mb-0.5">Key file</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 font-mono text-[11px] text-aubergine truncate select-all">{keyUrl}</code>
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(keyUrl)}
            data-testid="seo-indexnow-copy-key"
            className="inline-flex items-center gap-1 text-aubergine/65 hover:text-aubergine font-mono text-[10px] uppercase tracking-wider"
          >
            <Copy className="h-3 w-3" /> Copy
          </button>
          <a
            href={keyUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-aubergine/65 hover:text-champagne font-mono text-[10px] uppercase tracking-wider"
          >
            <ExternalLink className="h-3 w-3" /> Open
          </a>
        </div>
        <div className="mt-1 text-[10px] font-mono text-aubergine/45">Key: {settings.key.slice(0, 8)}…{settings.key.slice(-4)}</div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={pingHome}
          disabled={busy || !settings.enabled}
          data-testid="seo-indexnow-ping-now"
          className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2 font-mono text-xs disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-champagne" />}
          Ping search engines now
        </button>
        <button
          onClick={rotate}
          disabled={busy}
          data-testid="seo-indexnow-rotate"
          className="inline-flex items-center gap-1.5 rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-aubergine/70 disabled:opacity-60"
        >
          <RotateCcw className="h-3 w-3" /> Rotate key
        </button>
        {msg && <span className="font-mono text-[11px] text-aubergine/65" data-testid="seo-indexnow-msg">{msg}</span>}
      </div>

      {recent && recent.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 hover:text-aubergine">
            Recent activity ({recent.length})
          </summary>
          <ul className="mt-2 divide-y divide-aubergine/10 border border-aubergine/10 rounded-md" data-testid="seo-indexnow-log">
            {recent.slice(0, 8).map((p, i) => (
              <li key={i} className="px-3 py-2 flex items-center gap-3 text-[11px]">
                <span
                  className={`inline-block h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                    p.status >= 200 && p.status < 300 ? "bg-champagne" :
                    p.status === 202 ? "bg-champagne" :
                    p.status === 0 ? "bg-signal-red" :
                    "bg-champ-light"
                  }`}
                />
                <span className="font-mono text-aubergine/65 w-14 flex-shrink-0">{p.engine}</span>
                <span className="font-mono text-aubergine/75 w-10 flex-shrink-0 tabular-nums">{p.status || "err"}</span>
                <span className="text-aubergine/55 truncate flex-1">{(p.urls || []).slice(0, 2).join(", ")}</span>
                <span className="font-mono text-[10px] text-aubergine/45 flex-shrink-0">{fmtTime(p.created_at)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    if (diff < 60000) return "just now";
    if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`;
    return `${Math.round(diff / 86400000)}d ago`;
  } catch { return ""; }
}

// ---------------------------------------------------------------------------
// Analytics (Google Tag Manager) settings card.
// ---------------------------------------------------------------------------
function AnalyticsCard() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [testMsg, setTestMsg] = useState("");

  const reload = () =>
    api.get("/admin/seo/analytics").then((r) => {
      setData(r.data);
      setDraft(r.data);
    });

  useEffect(() => { reload(); }, []);

  if (!draft) {
    return (
      <div className="rounded-lg border border-aubergine/10 bg-white p-5 flex items-center justify-center h-24 text-aubergine/45">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  const patch = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));

  const idLooksValid = !draft.gtm_id || /^GTM-[A-Z0-9]{4,10}$/i.test(draft.gtm_id.trim());
  const dirty = data && JSON.stringify(data) !== JSON.stringify(draft);

  const save = async () => {
    setSaving(true);
    setError("");
    setMsg("");
    try {
      const payload = {
        gtm_id: draft.gtm_id,
        enabled: !!draft.enabled,
        spa_pageviews: !!draft.spa_pageviews,
        production_only: !!draft.production_only,
        environment: draft.environment || "production",
      };
      await api.patch("/admin/seo/analytics", payload);
      invalidateAnalyticsCache();
      await reload();
      setMsg("Analytics settings saved. Reload public pages to see GTM load.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const fireTestEvent = () => {
    setTestMsg("");
    if (typeof window === "undefined" || !window.dataLayer) {
      setTestMsg("dataLayer not initialised. Enable GTM and reload the public site first.");
      return;
    }
    window.dataLayer.push({
      event: "t_rex_test_ping",
      test_source: "admin_console",
      ts: Date.now(),
    });
    setTestMsg("Pushed t_rex_test_ping to dataLayer. Check GTM Preview or GA4 DebugView.");
  };

  return (
    <div className="rounded-lg border border-aubergine/10 bg-white p-5" data-testid="seo-analytics-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55">Analytics (Google Tag Manager)</p>
          <p className="text-sm text-aubergine/75 mt-1">
            Load a GTM container on the public site. Route changes are pushed as <code className="font-mono text-[11px] bg-bone/60 px-1 rounded">virtualPageview</code> events so GA4 sees every SPA navigation.
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider flex-shrink-0 ${
            data?.enabled && data?.gtm_id
              ? "bg-champagne/15 text-aubergine"
              : "bg-aubergine/8 text-aubergine/55"
          }`}
          data-testid="seo-analytics-status"
        >
          {data?.enabled && data?.gtm_id ? (
            <><CheckCircle2 className="h-3 w-3 text-champagne" /> Live</>
          ) : "Not connected"}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 mb-4">
        <Field label="GTM container ID" hint="Looks like GTM-ABC1234. Get yours at tagmanager.google.com.">
          <input
            value={draft.gtm_id || ""}
            onChange={(e) => patch("gtm_id", e.target.value.toUpperCase())}
            placeholder="GTM-XXXXXXX"
            data-testid="seo-analytics-gtm-id"
            className={`${inputCls} font-mono ${!idLooksValid ? "border-signal-red/60" : ""}`}
          />
          {!idLooksValid && (
            <p className="mt-1 text-[11px] text-signal-red">Must match <code className="font-mono">GTM-XXXXXXX</code>. GA4 measurement IDs (G-XXXXXX) go inside GTM, not here.</p>
          )}
        </Field>
        <Field label="Environment label" hint="Sent to GTM as an `env` dataLayer field so you can slice by environment.">
          <select
            value={draft.environment || "production"}
            onChange={(e) => patch("environment", e.target.value)}
            className={inputCls}
            data-testid="seo-analytics-env"
          >
            <option value="production">production</option>
            <option value="staging">staging</option>
            <option value="development">development</option>
          </select>
        </Field>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 mb-4">
        <label className="flex items-center gap-2 text-sm text-aubergine cursor-pointer" data-testid="seo-analytics-enabled">
          <input
            type="checkbox"
            checked={!!draft.enabled}
            onChange={(e) => patch("enabled", e.target.checked)}
            className="accent-champagne"
          />
          Load GTM on the public site
        </label>
        <label className="flex items-center gap-2 text-sm text-aubergine cursor-pointer" data-testid="seo-analytics-spa">
          <input
            type="checkbox"
            checked={!!draft.spa_pageviews}
            onChange={(e) => patch("spa_pageviews", e.target.checked)}
            className="accent-champagne"
          />
          Send <code className="font-mono text-[11px] bg-bone/60 px-1 rounded">virtualPageview</code> on route change
        </label>
        <label className="flex items-center gap-2 text-sm text-aubergine cursor-pointer" data-testid="seo-analytics-prod-only">
          <input
            type="checkbox"
            checked={!!draft.production_only}
            onChange={(e) => patch("production_only", e.target.checked)}
            className="accent-champagne"
          />
          Load only on the production host (skip preview / localhost)
        </label>
      </div>

      {error && (
        <div className="mb-3 rounded-md border border-signal-red/40 bg-signal-red/10 px-3 py-2 text-sm text-signal-red" data-testid="seo-analytics-error">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={save}
          disabled={saving || !idLooksValid || !dirty}
          data-testid="seo-analytics-save"
          className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2 font-mono text-xs disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save analytics
        </button>
        <button
          onClick={fireTestEvent}
          data-testid="seo-analytics-test"
          className="inline-flex items-center gap-1.5 rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-aubergine/70"
          title="Push a t_rex_test_ping event to the current page's dataLayer so you can verify GTM is receiving events."
        >
          <Sparkles className="h-3 w-3 text-champagne" /> Fire test event
        </button>
        {msg && <span className="font-mono text-[11px] text-aubergine/65" data-testid="seo-analytics-msg">{msg}</span>}
        {testMsg && <span className="font-mono text-[11px] text-aubergine/65" data-testid="seo-analytics-test-msg">{testMsg}</span>}
      </div>

      <details className="mt-4">
        <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 hover:text-aubergine">
          How to find your GTM container ID
        </summary>
        <ol className="mt-2 ml-4 list-decimal space-y-1 text-[12px] text-aubergine/75">
          <li>Go to <a href="https://tagmanager.google.com" target="_blank" rel="noreferrer" className="text-champagne hover:underline">tagmanager.google.com</a> and sign in.</li>
          <li>Create a container (or open the existing one for T-REX Network).</li>
          <li>The container ID sits at the top-right of the workspace and looks like <code className="font-mono">GTM-XXXXXXX</code>.</li>
          <li>Paste it above and enable {`"Load GTM on the public site"`}.</li>
          <li>Inside GTM, add your GA4 config tag (Measurement ID <code className="font-mono">G-XXXXXX</code>) and set its trigger to {`"All Pages"`} plus a custom event trigger for <code className="font-mono">virtualPageview</code>.</li>
        </ol>
      </details>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Cookie consent (GDPR banner) settings card.
// ---------------------------------------------------------------------------
function ConsentCard() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const reload = () =>
    api.get("/admin/seo/consent").then((r) => { setData(r.data); setDraft(r.data); });

  useEffect(() => { reload(); }, []);

  if (!draft) {
    return (
      <div className="rounded-lg border border-aubergine/10 bg-white p-5 flex items-center justify-center h-24 text-aubergine/45">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  const patch = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));
  const dirty = data && JSON.stringify(data) !== JSON.stringify(draft);

  const save = async (opts = {}) => {
    setSaving(true); setError(""); setMsg("");
    try {
      const payload = { ...draft };
      // Never send server-managed fields
      delete payload.version; delete payload.created_at; delete payload.updated_at;
      if (opts.bumpVersion) payload.bump_version = true;
      await api.patch("/admin/seo/consent", payload);
      await reload();
      setMsg(opts.bumpVersion
        ? "Saved. Banner version bumped, all returning visitors will be re-asked."
        : "Banner settings saved."
      );
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const openPreview = () => {
    if (typeof window !== "undefined" && window.__trex_previewCookieBanner) {
      window.__trex_previewCookieBanner();
      setMsg("Banner preview open. Refresh or click a button to close.");
    } else {
      setMsg("Open a public page in another tab to preview the banner.");
    }
  };

  return (
    <div className="rounded-lg border border-aubergine/10 bg-white p-5" data-testid="seo-consent-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55">Cookie consent (GDPR)</p>
          <p className="text-sm text-aubergine/75 mt-1">
            Shows a banner asking visitors to accept or reject non-essential cookies. GTM is gated on this choice via Google Consent Mode v2.
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider flex-shrink-0 ${
            data?.banner_enabled ? "bg-champagne/15 text-aubergine" : "bg-aubergine/8 text-aubergine/55"
          }`}
          data-testid="seo-consent-status"
        >
          {data?.banner_enabled ? (<><CheckCircle2 className="h-3 w-3 text-champagne" /> Live</>) : "Disabled"}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 mb-4">
        <label className="flex items-center gap-2 text-sm text-aubergine cursor-pointer" data-testid="seo-consent-enabled">
          <input type="checkbox" checked={!!draft.banner_enabled} onChange={(e) => patch("banner_enabled", e.target.checked)} className="accent-champagne" />
          Show banner
        </label>
        <Field label="Position">
          <select value={draft.banner_position || "bottom-right"} onChange={(e) => patch("banner_position", e.target.value)} className={inputCls} data-testid="seo-consent-position">
            <option value="bottom">bottom-center</option>
            <option value="bottom-left">bottom-left</option>
            <option value="bottom-right">bottom-right</option>
          </select>
        </Field>
        <Field label="Default policy" hint="opt_in = deny until accepted (EU). opt_out = grant analytics by default.">
          <select value={draft.default_policy || "opt_in"} onChange={(e) => patch("default_policy", e.target.value)} className={inputCls} data-testid="seo-consent-policy">
            <option value="opt_in">opt_in (EU compliant)</option>
            <option value="opt_out">opt_out</option>
          </select>
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 mb-4">
        <Field label="Title"><input value={draft.title || ""} onChange={(e) => patch("title", e.target.value)} className={inputCls} data-testid="seo-consent-title" /></Field>
        <Field label="Body">
          <textarea value={draft.body || ""} onChange={(e) => patch("body", e.target.value)} rows={2} className={`${inputCls} resize-none`} data-testid="seo-consent-body" />
        </Field>
        <Field label="Accept button"><input value={draft.accept_label || ""} onChange={(e) => patch("accept_label", e.target.value)} className={inputCls} data-testid="seo-consent-accept" /></Field>
        <Field label="Reject button"><input value={draft.reject_label || ""} onChange={(e) => patch("reject_label", e.target.value)} className={inputCls} data-testid="seo-consent-reject" /></Field>
        <Field label="Preferences button"><input value={draft.preferences_label || ""} onChange={(e) => patch("preferences_label", e.target.value)} className={inputCls} data-testid="seo-consent-prefs" /></Field>
        <Field label="Privacy policy URL"><input value={draft.policy_url || ""} onChange={(e) => patch("policy_url", e.target.value)} className={inputCls} data-testid="seo-consent-policy-url" /></Field>
      </div>

      <details className="mb-4">
        <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 hover:text-aubergine">
          Category descriptions (advanced)
        </summary>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label="Necessary — label"><input value={draft.necessary_label || ""} onChange={(e) => patch("necessary_label", e.target.value)} className={inputCls} /></Field>
          <Field label="Necessary — description"><input value={draft.necessary_description || ""} onChange={(e) => patch("necessary_description", e.target.value)} className={inputCls} /></Field>
          <Field label="Analytics — label"><input value={draft.analytics_label || ""} onChange={(e) => patch("analytics_label", e.target.value)} className={inputCls} /></Field>
          <Field label="Analytics — description"><input value={draft.analytics_description || ""} onChange={(e) => patch("analytics_description", e.target.value)} className={inputCls} /></Field>
          <Field label="Marketing — label"><input value={draft.marketing_label || ""} onChange={(e) => patch("marketing_label", e.target.value)} className={inputCls} /></Field>
          <Field label="Marketing — description"><input value={draft.marketing_description || ""} onChange={(e) => patch("marketing_description", e.target.value)} className={inputCls} /></Field>
        </div>
      </details>

      {error && <div className="mb-3 rounded-md border border-signal-red/40 bg-signal-red/10 px-3 py-2 text-sm text-signal-red" data-testid="seo-consent-error">{error}</div>}

      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => save()}
          disabled={saving || !dirty}
          data-testid="seo-consent-save"
          className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2 font-mono text-xs disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save banner
        </button>
        <button
          onClick={() => save({ bumpVersion: true })}
          disabled={saving}
          data-testid="seo-consent-bump"
          className="inline-flex items-center gap-1.5 rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-aubergine/70 disabled:opacity-60"
          title="Bumps the banner version so every returning visitor is asked again. Use when you materially change the copy."
        >
          <RotateCcw className="h-3 w-3" /> Ask all visitors again
        </button>
        <button
          onClick={openPreview}
          type="button"
          data-testid="seo-consent-preview"
          className="inline-flex items-center gap-1.5 rounded-md text-aubergine/65 hover:text-champagne font-mono text-[11px] uppercase tracking-wider"
        >
          <Sparkles className="h-3 w-3 text-champagne" /> Preview banner
        </button>
        {msg && <span className="font-mono text-[11px] text-aubergine/65" data-testid="seo-consent-msg">{msg}</span>}
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Page editor (drawer)
// ---------------------------------------------------------------------------
function PageEditor({ row, global, onClose, onSaved }) {
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    api.get("/admin/seo/pages/detail", { params: { path: row.path } })
      .then((r) => {
        const rec = r.data.exists ? r.data : {
          path: row.path,
          title: "",
          description: "",
          keywords: "",
          canonical_url: "",
          og_title: "",
          og_description: "",
          og_image_media_id: "",
          og_image_url: "",
          twitter_card: "",
          robots: "",
          h1: "",
          schema_json: "",
          enabled: true,
        };
        setExisting(r.data.exists);
        setForm(rec);
      });
  }, [row.path]);

  const patch = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put("/admin/seo/pages/detail", form);
      invalidateSEOCache(row.path);
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!existing) return;
    if (!window.confirm(`Reset ${row.path} to inherit from the global defaults?`)) return;
    setSaving(true);
    try {
      await api.delete("/admin/seo/pages/detail", { params: { path: row.path } });
      invalidateSEOCache(row.path);
      onSaved();
    } finally { setSaving(false); }
  };

  if (!form) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-aubergine/40 backdrop-blur-sm">
        <Loader2 className="h-6 w-6 animate-spin text-bone" />
      </div>
    );
  }

  const canonicalPreview = form.canonical_url
    || `${(global?.canonical_base_url || "").replace(/\/$/, "")}${row.path}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-aubergine/40 backdrop-blur-sm" data-testid="seo-page-editor">
      <div className="w-full max-w-3xl rounded-lg bg-white border border-aubergine/15 shadow-xl overflow-hidden max-h-[92vh] flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-aubergine/10 bg-bone/60 flex-shrink-0">
          <div>
            <div className="font-serif text-lg text-aubergine">
              <em className="accent-italic">SEO </em>· {row.label || row.path}
            </div>
            <div className="font-mono text-[11px] text-aubergine/55 mt-0.5">{row.path}</div>
          </div>
          <button onClick={onClose} data-testid="seo-editor-close" className="p-1 rounded hover:bg-aubergine/5 text-aubergine/55">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="p-5 space-y-5 overflow-y-auto flex-1">
          {error && (
            <div className="rounded-md border border-signal-red/40 bg-signal-red/10 px-4 py-2 text-sm text-signal-red" data-testid="seo-editor-error">
              {error}
            </div>
          )}

          {!existing && (
            <div className="rounded-md border border-aubergine/10 bg-bone/50 px-4 py-3 text-xs text-aubergine/70 flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              This page currently inherits everything from the global defaults. Fill in any field below to override.
            </div>
          )}

          <Field label="Title tag" hint="Unique title under 60 characters, containing your main keyword. The site title template is applied automatically.">
            <input
              value={form.title || ""}
              onChange={(e) => patch("title", e.target.value)}
              className={inputCls}
              data-testid="seo-editor-title"
              placeholder="e.g. Institutional tokenization infrastructure"
            />
            <CharCounter value={resolveTitle(form.title, global)} max={60} label="final" />
            <SharePreview
              title={resolveTitle(form.title, global) || global?.default_title || "T-REX Network"}
              rawTitle={form.title || ""}
              description={form.description || global?.default_description || ""}
              url={canonicalPreview}
              ogImageUrl={form.og_image_media_id ? undefined : (form.og_image_url || global?.default_og_image_url)}
              ogImageMediaId={form.og_image_media_id}
              twitterHandle={global?.twitter_handle}
              siteName={global?.site_name}
              ogTitle={form.og_title || form.title}
              ogDescription={form.og_description || form.description}
            />
          </Field>

          <Field label="Meta description" hint="Compelling summary under 160 characters. Shown as the snippet in search results.">
            <textarea
              value={form.description || ""}
              onChange={(e) => patch("description", e.target.value)}
              rows={3}
              className={`${inputCls} resize-none`}
              data-testid="seo-editor-description"
            />
            <CharCounter value={form.description || ""} max={160} />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Keywords" hint="Comma-separated.">
              <input value={form.keywords || ""} onChange={(e) => patch("keywords", e.target.value)} className={inputCls} data-testid="seo-editor-keywords" />
            </Field>
            <Field label="Robots" hint="Override the site default. E.g. noindex, nofollow.">
              <input value={form.robots || ""} onChange={(e) => patch("robots", e.target.value)} className={inputCls} data-testid="seo-editor-robots" placeholder={global?.robots || "index, follow"} />
            </Field>
          </div>

          <Field label="Canonical URL" hint="Leave blank to use the site default base + path.">
            <input value={form.canonical_url || ""} onChange={(e) => patch("canonical_url", e.target.value)} className={inputCls} data-testid="seo-editor-canonical" placeholder={canonicalPreview} />
          </Field>

          <div className="rounded-lg border border-aubergine/10 bg-bone/40 p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-3">Social share (Open Graph &amp; Twitter)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="OG title" hint="Defaults to the title tag if empty.">
                <input value={form.og_title || ""} onChange={(e) => patch("og_title", e.target.value)} className={inputCls} data-testid="seo-editor-og-title" />
              </Field>
              <Field label="OG description" hint="Defaults to the meta description if empty.">
                <input value={form.og_description || ""} onChange={(e) => patch("og_description", e.target.value)} className={inputCls} data-testid="seo-editor-og-description" />
              </Field>
              <Field label="OG image URL">
                <input value={form.og_image_url || ""} onChange={(e) => patch("og_image_url", e.target.value)} className={inputCls} data-testid="seo-editor-og-image-url" placeholder="https://…" />
              </Field>
              <MediaPickerField
                label="Or pick from Media library"
                value={form.og_image_media_id || ""}
                onChange={(id) => patch("og_image_media_id", id)}
                testid="seo-editor-og-image-media"
              />
              <Field label="Twitter card type">
                <select value={form.twitter_card || ""} onChange={(e) => patch("twitter_card", e.target.value)} className={inputCls} data-testid="seo-editor-twitter-card">
                  <option value="">{`Inherit (${global?.twitter_card || "summary_large_image"})`}</option>
                  <option value="summary">summary</option>
                  <option value="summary_large_image">summary_large_image</option>
                </select>
              </Field>
            </div>
          </div>

          <div className="rounded-lg border border-aubergine/10 bg-bone/40 p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-3">Structure &amp; schema</p>
            <Field label="Recommended H1" hint="Reference only. Use one single H1 tag per page in the code, followed by a logical H2/H3 hierarchy.">
              <input value={form.h1 || ""} onChange={(e) => patch("h1", e.target.value)} className={inputCls} data-testid="seo-editor-h1" />
            </Field>
            <div className="mt-4">
              <Field label="Custom JSON-LD schema (advanced)" hint="Overrides the auto-generated schema on this page. Must be valid JSON.">
                <textarea
                  value={form.schema_json || ""}
                  onChange={(e) => patch("schema_json", e.target.value)}
                  rows={6}
                  className={`${codeInputCls} resize-y`}
                  data-testid="seo-editor-schema"
                  placeholder='{ "@context": "https://schema.org", "@type": "Article", ... }'
                />
              </Field>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-aubergine/75 cursor-pointer" data-testid="seo-editor-enabled">
            <input
              type="checkbox"
              checked={form.enabled !== false}
              onChange={(e) => patch("enabled", e.target.checked)}
              className="accent-champagne"
            />
            Apply these overrides on the public site
          </label>
        </div>

        <footer className="flex items-center justify-end gap-3 px-5 py-3 border-t border-aubergine/10 bg-bone/40 flex-shrink-0">
          {existing && (
            <button
              onClick={reset}
              disabled={saving}
              data-testid="seo-editor-reset"
              className="mr-auto inline-flex items-center gap-1.5 text-aubergine/65 hover:text-signal-red font-mono text-[11px] uppercase tracking-wider"
              title="Delete this page's overrides. Falls back to global defaults."
            >
              <RotateCcw className="h-3 w-3" /> Reset to defaults
            </button>
          )}
          <a
            href={row.path.replace(/:[^/]+/g, "example")}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-aubergine/55 hover:text-champagne font-mono text-[11px] uppercase tracking-wider"
            title="Open the public page in a new tab"
          >
            <ExternalLink className="h-3 w-3" /> View
          </a>
          <button onClick={onClose} data-testid="seo-editor-cancel" className="rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-3 py-2 font-mono text-xs text-aubergine">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="seo-editor-save"
            className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2 font-mono text-xs disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save SEO
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live share preview: tabbed Google / LinkedIn / X card so admins can see
// exactly how each page will render before publishing SEO changes.
// ---------------------------------------------------------------------------
function SharePreview({ title, rawTitle, description, url, ogImageUrl, ogImageMediaId, twitterHandle, siteName, ogTitle, ogDescription }) {
  const [platform, setPlatform] = useState("google");
  const img = ogImageMediaId ? mediaUrl(ogImageMediaId) : ogImageUrl;
  const host = (() => { try { return new URL(url).host; } catch { return "t-rex.network"; } })();
  const displayUrl = (() => { try { const u = new URL(url); return `${u.host}${u.pathname}`; } catch { return url; } })();
  const shareTitle = ogTitle || rawTitle || title || siteName || "T-REX Network";
  const shareDesc = ogDescription || description || "";

  return (
    <div className="mt-3 rounded-md border border-aubergine/10 bg-white overflow-hidden" data-testid="seo-share-preview">
      <div className="flex items-center gap-3 px-3 pt-2 border-b border-aubergine/10 bg-bone/40">
        <div className="text-[10px] font-mono uppercase tracking-wider text-aubergine/45 flex items-center gap-1">
          <Sparkles className="h-3 w-3 text-champagne" /> Live preview
        </div>
        <div className="flex items-center gap-1 ml-auto">
          {[
            { id: "google", label: "Google" },
            { id: "linkedin", label: "LinkedIn" },
            { id: "twitter", label: "X" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setPlatform(t.id)}
              data-testid={`seo-preview-tab-${t.id}`}
              className={`px-2 py-1 -mb-px text-[10px] font-mono uppercase tracking-wider border-b-2 transition-colors ${
                platform === t.id
                  ? "border-champagne text-aubergine"
                  : "border-transparent text-aubergine/45 hover:text-aubergine"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4" data-testid={`seo-preview-${platform}`}>
        {platform === "google" && (
          <GooglePreview url={url} displayUrl={displayUrl} title={title} description={description} />
        )}
        {platform === "linkedin" && (
          <LinkedInPreview host={host} title={shareTitle} description={shareDesc} img={img} siteName={siteName} />
        )}
        {platform === "twitter" && (
          <XPreview handle={twitterHandle} host={host} title={shareTitle} description={shareDesc} img={img} />
        )}
      </div>
    </div>
  );
}

function GooglePreview({ url, displayUrl, title, description }) {
  return (
    <div className="font-sans">
      <div className="flex items-center gap-1.5 text-[12px] text-aubergine/70">
        <span className="inline-block h-4 w-4 rounded-full bg-champagne/20 border border-aubergine/10" />
        <span className="text-aubergine">T-REX Network</span>
        <span className="text-aubergine/45">·</span>
        <span className="truncate">{displayUrl}</span>
      </div>
      <div className="mt-0.5 text-[20px] leading-snug truncate cursor-pointer" style={{ color: "#1a0dab" }} title={title}>
        {(title || "").slice(0, 60) || "Page title"}
      </div>
      <div className="text-[13px] text-aubergine/75 mt-0.5 leading-snug">
        {(description || "").slice(0, 160) || <span className="italic text-aubergine/35">Meta description will appear here.</span>}
      </div>
    </div>
  );
}

function LinkedInPreview({ host, title, description, img, siteName }) {
  return (
    <div className="max-w-lg">
      <div className="rounded-md overflow-hidden border border-aubergine/15 bg-white">
        <div className="aspect-[1200/627] bg-bone/60 flex items-center justify-center overflow-hidden">
          {img ? (
            <img src={img} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="flex flex-col items-center gap-1 text-aubergine/35">
              <ImageIcon className="h-6 w-6" />
              <span className="font-mono text-[10px] uppercase tracking-wider">No OG image set</span>
            </div>
          )}
        </div>
        <div className="p-3 bg-bone/40">
          <div className="text-[13px] font-medium text-aubergine leading-snug line-clamp-2" title={title}>{title || "Page title"}</div>
          <div className="text-[11px] text-aubergine/55 mt-1 truncate">{host} · {siteName || "T-REX Network"}</div>
        </div>
      </div>
      <div className="mt-2 text-[11px] text-aubergine/55 line-clamp-2">
        {description ? `“${description}”` : <span className="italic text-aubergine/35">Post copy from OG description will appear here.</span>}
      </div>
    </div>
  );
}

function XPreview({ handle, host, title, description, img }) {
  return (
    <div className="max-w-lg">
      <div className="rounded-2xl overflow-hidden border border-aubergine/15 bg-white">
        <div className="aspect-[2/1] bg-bone/60 flex items-center justify-center overflow-hidden relative">
          {img ? (
            <img src={img} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="flex flex-col items-center gap-1 text-aubergine/35">
              <ImageIcon className="h-6 w-6" />
              <span className="font-mono text-[10px] uppercase tracking-wider">No OG image set</span>
            </div>
          )}
          <div className="absolute bottom-2 left-2 rounded bg-black/55 text-white text-[10px] px-1.5 py-0.5 backdrop-blur-sm truncate max-w-[80%]" title={title}>
            {title}
          </div>
        </div>
        <div className="px-3 py-2">
          <div className="text-[11px] text-aubergine/55">From {host}</div>
        </div>
      </div>
      <div className="mt-2 text-[12px] text-aubergine/75 line-clamp-3">
        {description || <span className="italic text-aubergine/35">Tweet description will appear here.</span>}
      </div>
      {handle && <div className="mt-1 text-[11px] text-aubergine/45">Attribution: {handle}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Media picker (image only): inline gallery, uploads through /admin/media
// ---------------------------------------------------------------------------
function MediaPickerField({ label, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/admin/media").then((r) => {
      setImages((r.data.media || []).filter((m) => (m.content_type || "").startsWith("image/")));
    }).finally(() => setLoading(false));
  }, [open]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("folder", "seo");
      const r = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.media_id);
      setOpen(false);
    } finally { setUploading(false); }
  };

  return (
    <Field label={label}>
      <div className="flex items-center gap-3">
        {value ? (
          <div className="h-14 w-14 rounded border border-aubergine/15 bg-bone flex items-center justify-center overflow-hidden">
            <img src={mediaUrl(value)} alt="" className="max-h-full max-w-full object-contain" />
          </div>
        ) : (
          <div className="h-14 w-14 rounded border border-dashed border-aubergine/20 bg-bone/40 flex items-center justify-center text-aubergine/30">
            <ImageIcon className="h-5 w-5" />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid={`${testid}-pick`}
            className="inline-flex items-center gap-1.5 rounded-md border border-aubergine/15 hover:bg-aubergine/5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-aubergine/70"
          >
            <ImageIcon className="h-3 w-3" /> Pick from library
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              data-testid={`${testid}-clear`}
              className="text-[10px] text-aubergine/50 hover:text-signal-red font-mono uppercase tracking-wider text-left"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-aubergine/50 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid={`${testid}-picker`}>
          <div className="w-full max-w-3xl max-h-[85vh] rounded-lg bg-white border border-aubergine/15 flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <header className="flex items-center justify-between px-5 py-3 border-b border-aubergine/10 bg-bone/60">
              <div className="font-serif text-base text-aubergine">Pick an image</div>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1.5 rounded-md border border-aubergine/15 hover:bg-aubergine/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-aubergine/70 cursor-pointer">
                  {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  Upload
                  <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
                </label>
                <button onClick={() => setOpen(false)} className="p-1 text-aubergine/55 hover:bg-aubergine/5 rounded"><X className="h-4 w-4" /></button>
              </div>
            </header>
            <div className="p-4 overflow-y-auto">
              {loading ? (
                <div className="py-16 text-center text-aubergine/55"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {images.map((m) => (
                    <button
                      type="button"
                      key={m.media_id}
                      onClick={() => { onChange(m.media_id); setOpen(false); }}
                      className={`aspect-square rounded overflow-hidden border ${value === m.media_id ? "border-champagne" : "border-aubergine/10 hover:border-champagne/50"} bg-bone/40 transition-colors`}
                      data-testid={`${testid}-item-${m.media_id}`}
                      title={m.original_filename}
                    >
                      <img src={mediaUrl(m.media_id)} alt={m.alt_text || m.original_filename} className="w-full h-full object-contain" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Field>
  );
}

// ---------------------------------------------------------------------------
// Audit panel
// ---------------------------------------------------------------------------
function AuditPanel({ audit, onEditPage, onAuditRefresh }) {
  const [bulkOpen, setBulkOpen] = useState(false);
  if (!audit) {
    return (
      <div className="py-16 flex items-center justify-center text-aubergine/55">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  const { pages, media } = audit;
  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="seo-audit-panel">
      <div className="rounded-lg border border-aubergine/10 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-aubergine/10 bg-bone/40">
          <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-aubergine/65">Page issues</p>
          <p className="text-xs text-aubergine/55 mt-1">{pages.with_issues.length} of {pages.total_records} managed pages need attention.</p>
        </div>
        {pages.with_issues.length === 0 ? (
          <div className="p-6 text-sm text-aubergine/55 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-champagne" /> All managed pages look healthy.
          </div>
        ) : (
          <ul className="divide-y divide-aubergine/10 max-h-[520px] overflow-y-auto">
            {pages.with_issues.map((p) => (
              <li key={p.path} className="px-4 py-3 hover:bg-bone/40 transition-colors cursor-pointer" onClick={() => onEditPage(p.path)} data-testid={`seo-audit-page-${p.path.replace(/[^a-z0-9]/gi, "-")}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-aubergine truncate">{p.title || p.path}</div>
                    <div className="text-[11px] font-mono text-aubergine/55">{p.path}</div>
                  </div>
                  <button className="text-champagne hover:text-aubergine font-mono text-[11px] uppercase tracking-wider flex-shrink-0">
                    Fix
                  </button>
                </div>
                <ul className="mt-2 space-y-1 text-xs">
                  {p.issues.map((i, idx) => (
                    <li key={idx} className={`flex items-start gap-1.5 ${i.level === "error" ? "text-signal-red" : i.level === "warn" ? "text-aubergine/75" : "text-aubergine/55"}`}>
                      {i.level === "error" ? <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" /> :
                        i.level === "warn" ? <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" /> :
                        <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />}
                      {i.message}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-lg border border-aubergine/10 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-aubergine/10 bg-bone/40 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-aubergine/65">Image alt text</p>
            <p className="text-xs text-aubergine/55 mt-1">
              {media.missing_alt_text_count} of {media.total_images} images are missing descriptive alt text.
            </p>
          </div>
          {media.missing_alt_text_count > 0 && (
            <button
              onClick={() => setBulkOpen(true)}
              data-testid="seo-audit-bulk-fill"
              className="inline-flex items-center gap-1.5 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider flex-shrink-0"
            >
              <Wand2 className="h-3 w-3" /> Bulk fill
            </button>
          )}
        </div>
        {media.missing_alt_text_count === 0 ? (
          <div className="p-6 text-sm text-aubergine/55 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-champagne" /> Every image has an alt text.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 p-4 max-h-[440px] overflow-y-auto">
              {media.missing_alt_text.map((m) => (
                <button
                  key={m.media_id}
                  onClick={() => setBulkOpen(true)}
                  className="aspect-square rounded border border-aubergine/10 bg-bone/40 overflow-hidden hover:border-champagne/50 transition-colors"
                  title={m.original_filename}
                  data-testid={`seo-audit-media-${m.media_id}`}
                >
                  <img src={mediaUrl(m.media_id)} alt={m.original_filename} className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
            <div className="px-4 py-3 border-t border-aubergine/10 bg-bone/40 text-xs text-aubergine/65">
              Click any thumbnail above, or use <button onClick={() => setBulkOpen(true)} className="text-champagne hover:underline font-mono uppercase tracking-wider">Bulk fill</button> to guide through them one by one.
            </div>
          </>
        )}
      </div>

      {bulkOpen && (
        <BulkAltFill
          items={media.missing_alt_text}
          onClose={() => { setBulkOpen(false); onAuditRefresh?.(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bulk alt-text fill: guided step-through of every image missing alt text.
// Each save PATCHes the media record and advances to the next item.
// ---------------------------------------------------------------------------
function BulkAltFill({ items: initialItems, onClose }) {
  const [items, setItems] = useState(initialItems || []);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [alt, setAlt] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const [error, setError] = useState("");

  // Fetch the full list so we can walk through ALL missing images (audit only
  // returns the first 100 for the tile grid).
  useEffect(() => {
    let alive = true;
    api.get("/admin/seo/audit/missing_alt").then((r) => {
      if (!alive) return;
      setItems(r.data.items || []);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const cur = items[idx];
  const total = items.length;
  const remaining = total - idx;

  useEffect(() => {
    setAlt("");
    setError("");
  }, [idx]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-aubergine/50 backdrop-blur-sm">
        <Loader2 className="h-6 w-6 animate-spin text-bone" />
      </div>
    );
  }

  if (!cur) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-aubergine/50 backdrop-blur-sm" data-testid="seo-bulk-alt-done">
        <div className="w-full max-w-md rounded-lg bg-white border border-aubergine/15 p-6 text-center">
          <CheckCircle2 className="h-8 w-8 text-champagne mx-auto mb-2" />
          <div className="font-serif text-xl text-aubergine mb-1">All done</div>
          <div className="text-sm text-aubergine/65 mb-4">Saved {done}, skipped {skipped}.</div>
          <button
            onClick={onClose}
            data-testid="seo-bulk-alt-close"
            className="rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2 font-mono text-xs"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const save = async (nextIdx = idx + 1) => {
    const value = alt.trim();
    if (!value) return;
    setSaving(true);
    setError("");
    try {
      await api.patch(`/admin/media/${cur.media_id}`, { alt_text: value });
      setDone((d) => d + 1);
      setIdx(nextIdx);
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const skip = () => {
    setSkipped((s) => s + 1);
    setIdx(idx + 1);
  };

  const suggestFromFilename = () => {
    let base = (cur.original_filename || "").replace(/\.[a-z0-9]+$/i, "");
    // Strip a leading UUID / hex / media-id token so filenames like
    // "6a1da7e0e09d4fee05ce053f_polygon-t-rex" become just "polygon-t-rex".
    base = base.replace(/^[0-9a-f]{8,}[-_ ]+/i, "");
    base = base.replace(/^med[-_][0-9a-f]{8,}[-_ ]+/i, "");
    const humanized = base
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
    setAlt(humanized);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-aubergine/50 backdrop-blur-sm" data-testid="seo-bulk-alt-fill">
      <div className="w-full max-w-2xl rounded-lg bg-white border border-aubergine/15 shadow-xl overflow-hidden flex flex-col max-h-[92vh]">
        <header className="flex items-center justify-between px-5 py-3 border-b border-aubergine/10 bg-bone/60">
          <div>
            <div className="font-serif text-lg text-aubergine">
              Alt text <em className="accent-italic">bulk fill</em>
            </div>
            <div className="font-mono text-[11px] text-aubergine/55 mt-0.5">
              {idx + 1} of {total} · {done} saved · {skipped} skipped · {remaining} remaining
            </div>
          </div>
          <button onClick={onClose} data-testid="seo-bulk-alt-cancel" className="p-1 rounded hover:bg-aubergine/5 text-aubergine/55">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          <div className="grid sm:grid-cols-2 gap-5 p-5">
            <div className="rounded-lg border border-aubergine/10 bg-bone/40 flex items-center justify-center overflow-hidden aspect-square">
              <img
                src={mediaUrl(cur.media_id)}
                alt={cur.original_filename}
                className="max-w-full max-h-full object-contain"
                data-testid="seo-bulk-alt-image"
              />
            </div>
            <div className="flex flex-col gap-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-aubergine/45 mb-1">Filename</div>
                <div className="text-sm text-aubergine break-all">{cur.original_filename}</div>
                <div className="text-[11px] font-mono text-aubergine/55 mt-0.5">{cur.folder}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-aubergine mb-1.5">Describe this image</label>
                <textarea
                  value={alt}
                  onChange={(e) => setAlt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save();
                  }}
                  rows={4}
                  autoFocus
                  data-testid="seo-bulk-alt-input"
                  placeholder="e.g. T-REX Network hero image showing tokenized flows across three jurisdictions"
                  className="w-full rounded-md border border-aubergine/15 bg-white px-3 py-2 text-sm outline-none focus:border-champagne resize-none"
                />
                <div className="mt-1 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={suggestFromFilename}
                    data-testid="seo-bulk-alt-suggest"
                    className="text-[10px] font-mono uppercase tracking-wider text-champagne hover:text-aubergine"
                  >
                    Suggest from filename
                  </button>
                  <span className="text-[10px] font-mono text-aubergine/40">Cmd+Enter to save</span>
                </div>
              </div>
              {error && <div className="text-xs text-signal-red">{error}</div>}
            </div>
          </div>
        </div>

        <footer className="flex items-center justify-between gap-3 px-5 py-3 border-t border-aubergine/10 bg-bone/40">
          <button
            onClick={() => setIdx(Math.max(0, idx - 1))}
            disabled={idx === 0}
            data-testid="seo-bulk-alt-prev"
            className="inline-flex items-center gap-1 rounded-md text-aubergine/65 hover:text-aubergine font-mono text-[11px] uppercase tracking-wider disabled:opacity-40"
          >
            <ChevronLeft className="h-3 w-3" /> Prev
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={skip}
              data-testid="seo-bulk-alt-skip"
              className="rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-3 py-2 font-mono text-xs text-aubergine"
            >
              Skip
            </button>
            <button
              onClick={() => save()}
              disabled={saving || !alt.trim()}
              data-testid="seo-bulk-alt-save"
              className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2 font-mono text-xs disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Save &amp; next
            </button>
            <button
              onClick={() => setIdx(Math.min(items.length, idx + 1))}
              data-testid="seo-bulk-alt-next"
              className="inline-flex items-center gap-1 rounded-md text-aubergine/65 hover:text-aubergine font-mono text-[11px] uppercase tracking-wider"
            >
              Next <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Utils
// ---------------------------------------------------------------------------
function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-aubergine mb-1.5">
        {label} {required && <span className="text-aubergine/45 font-normal">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-aubergine/55">{hint}</p>}
    </div>
  );
}
